import { useCallback, useEffect, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";

const MAX_FILE_SIZE = 10 * 1024 * 1024;

const TYPE_META = {
  pdf: { bg: "rgba(153,60,29,0.12)", color: "#993C1D", icon: "ri-file-pdf-line" },
  doc: { bg: "rgba(24,95,165,0.12)", color: "#185FA5", icon: "ri-file-word-line" },
  docx: { bg: "rgba(24,95,165,0.12)", color: "#185FA5", icon: "ri-file-word-line" },
  xls: { bg: "rgba(59,109,17,0.12)", color: "#3B6D11", icon: "ri-file-excel-line" },
  xlsx: { bg: "rgba(59,109,17,0.12)", color: "#3B6D11", icon: "ri-file-excel-line" },
  ppt: { bg: "rgba(216,90,48,0.12)", color: "#D85A30", icon: "ri-file-ppt-line" },
  pptx: { bg: "rgba(216,90,48,0.12)", color: "#D85A30", icon: "ri-file-ppt-line" },
  jpg: { bg: "rgba(186,117,23,0.12)", color: "#BA7517", icon: "ri-image-line" },
  jpeg: { bg: "rgba(186,117,23,0.12)", color: "#BA7517", icon: "ri-image-line" },
  png: { bg: "rgba(186,117,23,0.12)", color: "#BA7517", icon: "ri-image-line" },
  gif: { bg: "rgba(186,117,23,0.12)", color: "#BA7517", icon: "ri-image-line" },
  zip: { bg: "rgba(95,94,90,0.12)", color: "#5F5E5A", icon: "ri-file-zip-line" },
  rar: { bg: "rgba(95,94,90,0.12)", color: "#5F5E5A", icon: "ri-file-zip-line" },
  txt: { bg: "rgba(83,74,183,0.12)", color: "#534AB7", icon: "ri-file-text-line" },
  default: { bg: "rgba(83,74,183,0.12)", color: "#534AB7", icon: "ri-file-line" },
};

const getExt = (name) => (name?.split(".").pop() || "").toLowerCase();
const getMeta = (name) => TYPE_META[getExt(name)] || TYPE_META.default;
const fmtSize = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`);

const FileUpload = ({ onFilesChange, maxFiles = 20, accept }) => {
  const [files, setFiles] = useState([]);
  const [themeVars, setThemeVars] = useState({});
  const progressRefs = useRef({});

  useEffect(() => {
    const update = () => {
      const root = document.documentElement;
      setThemeVars({
        inputBg: getComputedStyle(root).getPropertyValue("--vz-input-bg-custom").trim(),
        bodyColor: getComputedStyle(root).getPropertyValue("--vz-body-color").trim(),
        borderColor: getComputedStyle(root).getPropertyValue("--vz-input-border-custom").trim(),
        primary: getComputedStyle(root).getPropertyValue("--vz-primary").trim(),
        light: getComputedStyle(root).getPropertyValue("--vz-light").trim(),
        mutedColor: getComputedStyle(root).getPropertyValue("--vz-secondary-color").trim(),
      });
    };
    update();
    const obs = new MutationObserver(update);
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["data-bs-theme", "class"] });
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    onFilesChange?.(files.map((f) => f.file));
  }, [files]);

  const simulateProgress = (id) => {
    let pct = 0;
    const interval = setInterval(() => {
      pct += Math.random() * 30 + 10;
      if (pct >= 100) {
        pct = 100;
        clearInterval(interval);
        setFiles((prev) => prev.map((f) => (f.id === id ? { ...f, status: "ready" } : f)));
      }
      if (progressRefs.current[id]) {
        progressRefs.current[id].style.width = Math.min(pct, 100) + "%";
      }
    }, 150);
  };

  const onDrop = useCallback((accepted, rejected) => {
    const validFiles = accepted.filter((f) => f.size <= MAX_FILE_SIZE);
    const newEntries = validFiles.map((file) => ({
      id: `${Date.now()}-${Math.random()}`,
      file,
      status: "uploading",
    }));
    setFiles((prev) => [...prev, ...newEntries]);
    newEntries.forEach((e) => simulateProgress(e.id));
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: true,
    maxFiles,
    accept,
    noClick: false,
  });

  const removeFile = (id) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const readyCount = files.filter((f) => f.status === "ready").length;
  const totalSize = files.reduce((a, f) => a + f.file.size, 0);

  const bg = themeVars.inputBg || "#fff";
  const border = themeVars.borderColor || "#ced4da";
  const text = themeVars.bodyColor || "#212529";
  const muted = themeVars.mutedColor || "#6c757d";
  const primary = themeVars.primary || "#405189";
  const lightBg = themeVars.light || "#f8f9fa";

  return (
    <div>
      {files.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10, marginBottom: 14 }}>
          {[
            { label: "Files", value: files.length },
            { label: "Total size", value: fmtSize(totalSize) },
            { label: "Ready", value: readyCount },
          ].map((s) => (
            <div key={s.label} style={{ background: lightBg, borderRadius: 8, padding: "10px 14px" }}>
              <div style={{ fontSize: 11, color: muted, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 3 }}>{s.label}</div>
              <div style={{ fontSize: 18, fontWeight: 600, color: text }}>{s.value}</div>
            </div>
          ))}
        </div>
      )}

      <div
        {...getRootProps()}
        style={{
          border: `2px dashed ${isDragActive ? primary : border}`,
          borderRadius: 10,
          padding: "2rem 1.5rem",
          textAlign: "center",
          cursor: "pointer",
          background: isDragActive ? `${primary}10` : bg,
          transition: "border-color 0.2s, background 0.2s",
        }}>
        <input {...getInputProps()} />
        <i className="ri-upload-cloud-2-line" style={{ fontSize: 40, color: primary, display: "block", marginBottom: 10 }} />
        <p style={{ fontSize: 15, fontWeight: 600, color: text, marginBottom: 6 }}>
          {isDragActive ? "Drop files here..." : "Drag & drop files here"}
        </p>
        <p style={{ fontSize: 13, color: muted, marginBottom: 16 }}>or click to browse — max 10MB per file</p>

        <div style={{ display: "flex", justifyContent: "center", gap: 8, flexWrap: "wrap" }}>
          {[
            { ext: "PDF", icon: "ri-file-pdf-line", color: "#993C1D", bg: "rgba(153,60,29,0.1)" },
            { ext: "DOCX", icon: "ri-file-word-line", color: "#185FA5", bg: "rgba(24,95,165,0.1)" },
            { ext: "XLSX", icon: "ri-file-excel-line", color: "#3B6D11", bg: "rgba(59,109,17,0.1)" },
            { ext: "Image", icon: "ri-image-line", color: "#BA7517", bg: "rgba(186,117,23,0.1)" },
            { ext: "ZIP", icon: "ri-file-zip-line", color: "#5F5E5A", bg: "rgba(95,94,90,0.1)" },
          ].map((t) => (
            <span
              key={t.ext}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                fontSize: 12,
                padding: "3px 10px",
                border: `0.5px solid ${t.color}40`,
                borderRadius: 20,
                color: t.color,
                background: t.bg,
              }}>
              <i className={t.icon} style={{ fontSize: 13 }} />
              {t.ext}
            </span>
          ))}
        </div>
      </div>

      {files.length > 0 && (
        <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 8 }}>
          {files.map((f) => {
            const meta = getMeta(f.file.name);
            const ext = getExt(f.file.name).toUpperCase() || "FILE";
            return (
              <div
                key={f.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "10px 14px",
                  border: `0.5px solid ${border}`,
                  borderRadius: 8,
                  background: bg,
                  animation: "fadeSlideIn 0.2s ease",
                }}>
                <div
                  style={{
                    width: 38,
                    height: 38,
                    borderRadius: 8,
                    flexShrink: 0,
                    background: meta.bg,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}>
                  <i className={meta.icon} style={{ fontSize: 18, color: meta.color }} />
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3, flexWrap: "wrap" }}>
                    <span
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: text,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: 220,
                      }}>
                      {f.file.name}
                    </span>
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        padding: "1px 6px",
                        borderRadius: 4,
                        background: meta.bg,
                        color: meta.color,
                        textTransform: "uppercase",
                      }}>
                      {ext}
                    </span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 12, color: muted }}>{fmtSize(f.file.size)}</span>
                    {f.status === "ready" ? (
                      <span
                        style={{
                          fontSize: 11,
                          padding: "1px 8px",
                          borderRadius: 20,
                          background: "rgba(10,179,156,0.15)",
                          color: "#0ab39c",
                          fontWeight: 500,
                        }}>
                        <i className="ri-checkbox-circle-line me-1" />
                        Ready
                      </span>
                    ) : (
                      <span style={{ fontSize: 11, padding: "1px 8px", borderRadius: 20, background: lightBg, color: muted, fontWeight: 500 }}>
                        Uploading...
                      </span>
                    )}
                  </div>
                  <div style={{ marginTop: 6, height: 3, borderRadius: 2, background: `${border}60`, overflow: "hidden" }}>
                    <div
                      ref={(el) => {
                        if (el) progressRefs.current[f.id] = el;
                      }}
                      style={{
                        height: "100%",
                        borderRadius: 2,
                        background: f.status === "ready" ? "#0ab39c" : primary,
                        width: f.status === "ready" ? "100%" : "0%",
                        transition: "width 0.3s ease",
                      }}
                    />
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => removeFile(f.id)}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: muted,
                    fontSize: 18,
                    padding: 2,
                    borderRadius: 4,
                    flexShrink: 0,
                    lineHeight: 1,
                  }}
                  title="Remove file">
                  <i className="ri-close-line" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      <style>{`
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(-5px); }
          to   { opacity: 1; transform: translateY(0);    }
        }
      `}</style>
    </div>
  );
};

export default FileUpload;
